import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import CampaignAutoContent from '../components/CampaignAutoContent';

const CampaignForm = () => {
  const { companyId, projectId, campaignId } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!campaignId;

  const [company, setCompany] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showAIMenu, setShowAIMenu] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiPreview, setAiPreview] = useState(null);
  const [aiPlan, setAiPlan] = useState(null);
  const [showAIPlanDialog, setShowAIPlanDialog] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    campaign_type: 'email',
    channel: '',
    budget: '',
    start_date: '',
    end_date: '',
    status: 'draft'
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch company
        const companyRes = await api.get(`/companies/${companyId}`);
        setCompany(companyRes.data);
        
        // Fetch project
        const projectRes = await api.get(`/companies/${companyId}/projects/${projectId}`);
        setProject(projectRes.data);
        
        // If edit mode, fetch campaign data
        if (isEditMode) {
          const campaignRes = await api.get(`/projects/${projectId}/campaigns/${campaignId}`);
          const campaign = campaignRes.data;
          
          setFormData({
            name: campaign.name || '',
            slug: campaign.slug || '',
            description: campaign.description || '',
            campaign_type: campaign.campaign_type || 'email',
            channel: campaign.channel || '',
            budget: campaign.budget !== null && campaign.budget !== undefined ? campaign.budget : '',
            start_date: campaign.start_date ? campaign.start_date.slice(0, 16) : '',
            end_date: campaign.end_date ? campaign.end_date.slice(0, 16) : '',
            status: campaign.status || 'draft'
          });
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [companyId, projectId, campaignId, isEditMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'name' && !isEditMode && { 
        slug: value.toLowerCase().replace(/[^a-z0-9]+/g, '-') 
      })
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const payload = {
        ...formData,
        budget: formData.budget ? parseFloat(formData.budget) : null,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null
      };

      if (isEditMode) {
        await api.put(`/projects/${projectId}/campaigns/${campaignId}`, payload);
      } else {
        await api.post(`/projects/${projectId}/campaigns`, payload);
      }

      navigate(`/companies/${companyId}/projects/${projectId}/edit`);
    } catch (err) {
      console.error('Error saving campaign:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) {
      return;
    }

    try {
      setSaving(true);
      await api.delete(`/projects/${projectId}/campaigns/${campaignId}`);
      navigate(`/companies/${companyId}/projects/${projectId}/edit`);
    } catch (err) {
      console.error('Error deleting campaign:', err);
      setError(err.response?.data?.error || err.response?.data?.message || 'Failed to delete campaign');
      setSaving(false);
    }
  };

  const handleAIRewrite = async () => {
    if (!isEditMode) {
      setError('Please save the campaign first before using AI rewrite');
      return;
    }
    
    try {
      setAiLoading(true);
      setError(null);
      
      const previewResponse = await api.post(`/campaigns/${campaignId}/ai-rewrite`, { apply: false });
      setAiPreview(previewResponse.data.preview);
      
      if (window.confirm('AI has generated enhanced content. Would you like to apply these changes?')) {
        const applyResponse = await api.post(`/campaigns/${campaignId}/ai-rewrite`, { apply: true });
        
        setFormData(prev => ({
          ...prev,
          name: applyResponse.data.campaign.name,
          description: applyResponse.data.campaign.description
        }));
        
        setError(null);
        alert('Campaign successfully enhanced with AI! ✨');
      }
    } catch (error) {
      console.error('Error rewriting with AI:', error);
      setError(error.response?.data?.error || 'Failed to rewrite with AI');
    } finally {
      setAiLoading(false);
      setShowAIMenu(false);
    }
  };

  const handleAICreatePlan = async () => {
    if (!isEditMode) {
      setError('Please save the campaign first before generating a plan');
      return;
    }
    
    try {
      setAiLoading(true);
      setError(null);
      
      const planResponse = await api.post(`/campaigns/${campaignId}/ai-generate-plan`, { apply: false });
      setAiPlan(planResponse.data.plan);
      setShowAIPlanDialog(true);
    } catch (error) {
      console.error('Error generating plan:', error);
      setError(error.response?.data?.error || 'Failed to generate plan');
    } finally {
      setAiLoading(false);
      setShowAIMenu(false);
    }
  };

  const handleApplyAIPlan = async () => {
    try {
      setAiLoading(true);
      
      const response = await api.post(`/campaigns/${campaignId}/ai-generate-plan`, { apply: true });
      
      alert(`Success! Created ${response.data.total_tasks} AI agent tasks for this campaign. 🎯`);
      setShowAIPlanDialog(false);
      setAiPlan(null);
    } catch (error) {
      console.error('Error applying plan:', error);
      setError(error.response?.data?.error || 'Failed to apply plan');
    } finally {
      setAiLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      draft: 'badge-secondary',
      scheduled: 'badge-info',
      running: 'badge-success',
      paused: 'badge-warning',
      completed: 'badge-primary',
      cancelled: 'badge-danger'
    };
    return colors[status] || 'badge-secondary';
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      low: 'badge-secondary',
      medium: 'badge-info',
      high: 'badge-warning',
      critical: 'badge-danger'
    };
    return colors[priority] || 'badge-secondary';
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button
          onClick={() => navigate('/companies')}
          className="breadcrumb-link"
        >
          Companies
        </button>
        <span className="breadcrumb-separator">/</span>
        <button
          onClick={() => navigate(`/companies/${companyId}/edit`)}
          className="breadcrumb-link"
        >
          {company?.name}
        </button>
        <span className="breadcrumb-separator">/</span>
        <button
          onClick={() => navigate(`/companies/${companyId}/projects/${projectId}/edit`)}
          className="breadcrumb-link"
        >
          {project?.name}
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">
          {isEditMode ? 'Edit Campaign' : 'New Campaign'}
        </span>
      </nav>

      {/* Campaign Form Card */}
      <div className="card">
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              {isEditMode ? 'Edit Campaign' : 'Create New Campaign'}
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              For {project?.name} in {company?.name}
            </p>
          </div>
        </div>

        {error && (
          <div style={{
            backgroundColor: 'var(--red-50)',
            border: '1px solid var(--red-200)',
            color: 'var(--red-800)',
            padding: '0.75rem',
            borderRadius: '8px',
            marginBottom: '1rem'
          }}>
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ 
          display: 'flex', 
          gap: '1rem', 
          justifyContent: 'space-between',
          marginBottom: '1.5rem',
          marginTop: '5px',
          paddingBottom: '1.5rem',
          borderBottom: '1px solid var(--gray-200)'
        }}>
          <div style={{ display: 'flex', gap: '1rem' }}>
            {isEditMode && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={saving}
                className="btn btn-danger"
              >
                🗑️ Delete
              </button>
            )}
          </div>
          
          <div style={{ display: 'flex', gap: '1rem' }}>
            {isEditMode && (
              <div style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => setShowAIMenu(!showAIMenu)}
                  disabled={saving || aiLoading}
                  className="btn btn-primary"
                  style={{ backgroundColor: '#7C3AED', borderColor: '#7C3AED' }}
                >
                  🤖 AI
                </button>
                
                {showAIMenu && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    right: 0,
                    marginTop: '0.5rem',
                    backgroundColor: 'white',
                    border: '1px solid var(--gray-300)',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    zIndex: 1000,
                    minWidth: '250px'
                  }}>
                    <button
                      onClick={handleAIRewrite}
                      disabled={aiLoading}
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        textAlign: 'left',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        borderBottom: '1px solid var(--gray-200)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--gray-50)'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                      <span>✨</span>
                      <div>
                        <div style={{ fontWeight: '600', color: 'var(--gray-900)' }}>
                          Rewrite Campaign with AI
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)' }}>
                          Enhance descriptions and fields
                        </div>
                      </div>
                    </button>
                    
                    <button
                      onClick={handleAICreatePlan}
                      disabled={aiLoading}
                      style={{
                        width: '100%',
                        padding: '0.75rem 1rem',
                        textAlign: 'left',
                        border: 'none',
                        background: 'none',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                      onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--gray-50)'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                      <span>📋</span>
                      <div>
                        <div style={{ fontWeight: '600', color: 'var(--gray-900)' }}>
                          Create Execution Plan
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--gray-600)' }}>
                          Generate AI agent tasks
                        </div>
                      </div>
                    </button>
                  </div>
                )}
              </div>
            )}
            
            <button
              type="button"
              onClick={() => navigate(`/companies/${companyId}/projects/${projectId}/edit`)}
              disabled={saving}
              className="btn btn-outline"
            >
              ✕ Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="btn btn-primary"
              form="campaign-form"
              title="Update Campaign"
            >
              {saving ? 'Saving...' : (isEditMode ? '💾 Update' : 'Create Campaign')}
            </button>
          </div>
        </div>

        <form id="campaign-form" onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label className="form-label">
                Campaign Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="form-input"
                placeholder="Enter campaign name"
              />
            </div>
            <div>
              <label className="form-label">
                Slug
              </label>
              <input
                type="text"
                name="slug"
                value={formData.slug}
                onChange={handleInputChange}
                className="form-input"
                placeholder="campaign-slug"
              />
            </div>
          </div>

          <div style={{ marginBottom: '1rem' }}>
            <label className="form-label">
              Description
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              className="form-input"
              rows="6"
              placeholder="Brief description of the campaign"
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label className="form-label">
                Campaign Type *
              </label>
              <select
                name="campaign_type"
                value={formData.campaign_type}
                onChange={handleInputChange}
                className="form-input"
                required
              >
                <option value="email">Email Marketing</option>
                <option value="social">Social Media</option>
                <option value="paid_ads">Paid Advertising</option>
                <option value="seo">SEO/Organic</option>
                <option value="content">Content Marketing</option>
                <option value="webinar">Webinar</option>
                <option value="event">Event</option>
                <option value="influencer">Influencer</option>
              </select>
            </div>
            <div>
              <label className="form-label">
                Channel/Platform
              </label>
              <input
                type="text"
                name="channel"
                value={formData.channel}
                onChange={handleInputChange}
                className="form-input"
                placeholder="e.g., Facebook, Google"
              />
            </div>
            <div>
              <label className="form-label">
                Budget
              </label>
              <input
                type="number"
                name="budget"
                value={formData.budget}
                onChange={handleInputChange}
                className="form-input"
                placeholder="5000"
                min="0"
                step="0.01"
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
            <div>
              <label className="form-label">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="form-input"
              >
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="running">Running</option>
                <option value="paused">Paused</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div>
              <label className="form-label">
                Start Date
              </label>
              <input
                type="datetime-local"
                name="start_date"
                value={formData.start_date}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>
            <div>
              <label className="form-label">
                End Date
              </label>
              <input
                type="datetime-local"
                name="end_date"
                value={formData.end_date}
                onChange={handleInputChange}
                className="form-input"
              />
            </div>
          </div>
        </form>
      </div>

      {/* Auto-Content Generation Section */}
      {isEditMode && (
        <div style={{ marginTop: '1.5rem' }}>
          <CampaignAutoContent 
            campaignId={parseInt(campaignId)}
            projectId={parseInt(projectId)}
            onContentGenerated={(content) => {
            }}
          />
        </div>
      )}

      {/* AI Plan Dialog */}
      {showAIPlanDialog && aiPlan && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{
            width: '90%',
            maxWidth: '900px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '1.5rem',
              paddingBottom: '1rem',
              borderBottom: '2px solid var(--primary-600)'
            }}>
              <h3 style={{ margin: 0, color: 'var(--gray-900)' }}>
                🤖 AI-Generated Campaign Execution Plan
              </h3>
              <button
                onClick={() => setShowAIPlanDialog(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: 'var(--gray-500)'
                }}
              >
                ✕
              </button>
            </div>

            <div style={{
              backgroundColor: 'var(--primary-50)',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1.5rem'
            }}>
              <p style={{ margin: 0, color: 'var(--gray-700)' }}>
                {aiPlan.plan_summary}
              </p>
              <div style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: 'var(--gray-600)' }}>
                <strong>Estimated Duration:</strong> {aiPlan.total_estimated_days} days
              </div>
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              {aiPlan.phases && aiPlan.phases.map((phase, phaseIndex) => (
                <div key={phaseIndex} style={{
                  marginBottom: '1.5rem',
                  border: '1px solid var(--gray-200)',
                  borderRadius: '8px',
                  padding: '1rem'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1rem',
                    paddingBottom: '0.5rem',
                    borderBottom: '1px solid var(--gray-200)'
                  }}>
                    <h4 style={{ margin: 0, color: 'var(--gray-900)' }}>
                      {phase.phase_name}
                    </h4>
                    <span style={{
                      backgroundColor: 'var(--gray-100)',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '12px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      color: 'var(--gray-700)'
                    }}>
                      {phase.duration_days} days
                    </span>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {phase.tasks && phase.tasks.map((task, taskIndex) => (
                      <div key={taskIndex} style={{
                        padding: '0.75rem',
                        backgroundColor: 'var(--gray-50)',
                        borderRadius: '6px',
                        borderLeft: `3px solid ${
                          task.priority === 'high' ? '#EF4444' :
                          task.priority === 'medium' ? '#F59E0B' :
                          '#10B981'
                        }`
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'start',
                          marginBottom: '0.5rem'
                        }}>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontWeight: '600',
                              color: 'var(--gray-900)',
                              marginBottom: '0.25rem'
                            }}>
                              {task.name}
                            </div>
                            <div style={{
                              fontSize: '0.875rem',
                              color: 'var(--gray-600)',
                              marginBottom: '0.5rem'
                            }}>
                              {task.description}
                            </div>
                            <div style={{
                              display: 'flex',
                              gap: '0.5rem',
                              flexWrap: 'wrap',
                              fontSize: '0.75rem'
                            }}>
                              <span style={{
                                backgroundColor: 'white',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                border: '1px solid var(--gray-300)'
                              }}>
                                👤 {task.agent_type?.replace('_', ' ') || 'General'}
                              </span>
                              <span style={{
                                backgroundColor: 'white',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px',
                                border: '1px solid var(--gray-300)'
                              }}>
                                ⏱️ {task.duration_days} days
                              </span>
                              <span style={{
                                backgroundColor: task.can_automate ? 'var(--success-100)' : 'var(--warning-100)',
                                color: task.can_automate ? 'var(--success-700)' : 'var(--warning-700)',
                                padding: '0.25rem 0.5rem',
                                borderRadius: '4px'
                              }}>
                                {task.can_automate ? '🤖 Automated' : '👨 Manual'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'flex-end',
              paddingTop: '1rem',
              borderTop: '1px solid var(--gray-200)'
            }}>
              <button
                onClick={() => setShowAIPlanDialog(false)}
                className="btn btn-outline"
                disabled={aiLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleApplyAIPlan}
                className="btn btn-primary"
                disabled={aiLoading}
              >
                {aiLoading ? 'Creating Tasks...' : '✓ Apply Plan & Create Tasks'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignForm;
